import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      // QA round 1, F-16 — blocker — and F-135.
      //
      // A type="number" input silently discards letters, so typing "abc" left
      // value === "". The field is not `required`, so checkValidity() returned
      // true and validationMessage was empty — an empty value is a "valid" one.
      // This handler then posted that empty string, Shopify read it as 0, and
      // the line was removed. Measured: a cart of 999 went to 0, with no
      // message, no confirmation, and no undo anywhere on the site.
      //
      // min="1" was present the whole time and did nothing, because the update
      // is submitted by JavaScript on blur rather than by form submission, so
      // constraint validation never runs.
      //
      // An unreadable value now restores what was there rather than being sent.
      // Deleting a line still works through the Remove button, which is
      // explicit, labelled, and the only thing that should destroy anything.
      const typed = event.target.value.trim();
      const parsed = Number.parseInt(typed, 10);

      if (typed === '' || Number.isNaN(parsed) || parsed < 1) {
        const restored = this.refs.quantityInput.defaultValue || '1';
        this.refs.quantityInput.value = restored;
        event.target.value = restored;
        return;
      }

      this.refs.quantityInput.value = String(parsed);
    }
    this.#onQuantityChange();
  }

  /**
   * QA round 1, F-128. The input sits inside <form id="cart-form" method="post"
   * action="/cart">, so pressing Enter implicitly submitted it — a full-page
   * POST navigation. Blurring commits the identical edit over AJAX in about
   * 1.2s without leaving the page. Same control, same intent, two completely
   * different outcomes depending on how the customer finished typing; from the
   * drawer it threw away whatever page they were on.
   *
   * Enter now commits exactly the way blur does.
   * @param {KeyboardEvent} event
   */
  handleQuantityKeydown(event) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.setQuantity(event);
    if (event.target instanceof HTMLElement) event.target.blur();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const { min, max, value: newValue } = quantityInput;

    if (newValue < min && min) quantityInput.value = min;
    if (newValue > max && max) quantityInput.value = max;
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
